import { AVAILABILITY_HORIZON_DAYS, OFFERED_DAYS } from "../constants";
import { addCompactDays } from "./sap-format";
import type { AppointmentDay } from "../types";

// The date picker's calendar works in local-midnight `Date`s; the flow works
// in compact YYYYMMDD strings in the customer's zone. Local year/month/day
// are used in both directions — never UTC — so the day the customer tapped
// is the day sent to SAP.

/** "YYYYMMDD" -> local midnight of that calendar day. */
export function compactToLocalDate(yyyymmdd: string): Date {
  const year = Number(yyyymmdd.slice(0, 4));
  const month = Number(yyyymmdd.slice(4, 6));
  const day = Number(yyyymmdd.slice(6, 8));
  return new Date(year, month - 1, day);
}

/** A local Date's calendar day -> "YYYYMMDD". */
export function localDateToCompact(date: Date): string {
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}${mm}${dd}`;
}

export interface PickerBoundsInput {
  /** The strip the first load produced. */
  initialDays: readonly Pick<AppointmentDay, "date">[];
  /** Today in the customer's zone, YYYYMMDD. */
  today: string;
}

export interface PickerBounds {
  /** Earliest pickable day: the day after the initial strip's last chip. */
  min: string;
  /** Latest pickable day: today + the availability horizon. */
  max: string;
}

/**
 * The calendar's selectable range, or `null` when there is no picker: the
 * initial search only stops short of `OFFERED_DAYS` days after sweeping
 * every day to the horizon, so a strip with fewer chips has nothing left
 * for a pick to find; likewise when the third chip is already the horizon.
 */
export function pickerBounds(input: PickerBoundsInput): PickerBounds | null {
  const { initialDays, today } = input;
  if (initialDays.length !== OFFERED_DAYS) return null;
  const last = initialDays[initialDays.length - 1];
  if (!last) return null;
  const min = addCompactDays(last.date, 1);
  const max = addCompactDays(today, AVAILABILITY_HORIZON_DAYS);
  return min > max ? null : { min, max };
}

export interface PickedDays {
  /** The strip to show: the picked day first, then what follows. */
  days: AppointmentDay[];
  /** SAP offered nothing on the picked day; its chip carries no windows. */
  pickedUnavailable: boolean;
}

/**
 * The strip after a pick. The search starts at the picked day, so it is
 * available iff it is the first day returned; then the strip is the first
 * `OFFERED_DAYS` days. Otherwise the picked day leads the strip as an
 * unavailable chip (no windows) and the next `OFFERED_DAYS - 1` returned
 * days follow it.
 */
export function applyPickedDays(
  picked: string,
  fetched: AppointmentDay[],
): PickedDays {
  if (fetched[0]?.date === picked) {
    return { days: fetched.slice(0, OFFERED_DAYS), pickedUnavailable: false };
  }
  return {
    days: [
      { date: picked, windows: [] },
      ...fetched.slice(0, OFFERED_DAYS - 1),
    ],
    pickedUnavailable: true,
  };
}
